import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReelEditor } from '../components/editor/reels/ReelEditor';
import { useActiveListing } from '../contexts/ActiveListingContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Video, Clock, Trash2, Play, Film, Copy, Edit,
  Layers, ArrowRight, Sparkles, Loader2, Calendar, Home
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import { MobileTabBar } from '../components/MobileTabBar';
import { AppNavbar } from '../components/AppNavbar';

function ReelTemplatePicker({ onSelect, onCreate }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/reels/templates');
        setTemplates(res.data?.templates || res.data || []);
      } catch {
        toast.error('Failed to load reel templates');
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  if (templates.length === 0) {
    return (
      <div className="text-center py-16">
        <Film className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
        <p className="text-muted-foreground mb-4">No reel templates available yet</p>
        <Button onClick={onCreate}><Plus className="h-4 w-4 mr-1" /> Start with Blank Reel</Button>
      </div>
    );
  }

  const REEL_STYLES = {
    'just listed': { gradient: 'from-emerald-600 to-teal-800', label: 'JUST LISTED', accent: '#10B981' },
    'open house': { gradient: 'from-blue-600 to-indigo-800', label: 'OPEN HOUSE', accent: '#3B82F6' },
    'just sold': { gradient: 'from-red-600 to-rose-800', label: 'SOLD', accent: '#EF4444' },
    'coming soon': { gradient: 'from-violet-600 to-purple-900', label: 'COMING SOON', accent: '#8B5CF6' },
    'price reduced': { gradient: 'from-amber-500 to-orange-700', label: 'PRICE DROP', accent: '#F59E0B' },
  };

  const getReelStyle = (name) => {
    const key = Object.keys(REEL_STYLES).find(k => name.toLowerCase().includes(k));
    return REEL_STYLES[key] || { gradient: 'from-slate-700 to-slate-900', label: name.toUpperCase(), accent: '#6366F1' };
  };

  return (
    <div data-testid="reel-template-picker">
      <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
        {templates.map(tpl => {
          const tracks = tpl.tracks_config || tpl.tracks || [];
          const totalDur = tracks.reduce((s, t) => s + (t.duration || 3), 0);
          const style = getReelStyle(tpl.name);
          return (
            <button
              key={tpl.template_id}
              type="button"
              className="group text-left rounded-2xl border border-gray-200 bg-white hover:border-gray-400 hover:shadow-md transition-all overflow-hidden active:scale-[0.98]"
              onClick={() => onSelect(tpl.template_id)}
              data-testid={`reel-tpl-pick-${tpl.template_id}`}
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-gray-50">
                {tpl.thumbnail_url ? (
                  <img src={tpl.thumbnail_url} alt={tpl.name} className="w-full h-full object-cover" />
                ) : (
                  <div className={`w-full h-full bg-gradient-to-br ${style.gradient} flex flex-col items-center justify-center p-4`}>
                    <div className="w-20 h-32 border-2 border-white/30 rounded-lg flex flex-col items-center justify-center gap-1 relative">
                      <div className="absolute top-1 left-1/2 -translate-x-1/2 w-6 h-1 bg-white/20 rounded-full" />
                      <span className="text-[8px] font-bold text-white/90 tracking-wider text-center leading-tight px-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {style.label}
                      </span>
                      <div className="flex gap-0.5 mt-0.5">
                        {tracks.slice(0, 4).map((_, i) => (
                          <div key={i} className="w-3 h-1.5 rounded-full bg-white/30" />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <Play className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                </div>
                <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Clock className="h-2.5 w-2.5" /> {totalDur.toFixed(0)}s
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-sm truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{tpl.name}</h3>
                <div className="flex items-center justify-between mt-2">
                  <span className="flex items-center gap-1 text-[11px] text-gray-400">
                    <Layers className="h-3 w-3" /> {tracks.length} clips
                  </span>
                  <span className="text-[11px] font-medium text-gray-500 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Use <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SavedReelsList({ onSelect, onCreate }) {
  const [compositions, setCompositions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCompositions(); }, []);

  const fetchCompositions = async () => {
    try {
      const res = await apiClient.get('/reels/compositions');
      setCompositions(res.data.compositions || []);
    } catch {
      toast.error('Failed to load saved reels');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e, compId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this reel?')) return;
    try {
      await apiClient.delete(`/reels/compositions/${compId}`);
      toast.success('Reel deleted');
      fetchCompositions();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleClone = async (e, compId) => {
    e.stopPropagation();
    try {
      const res = await apiClient.post(`/reels/compositions/${compId}/clone`);
      toast.success('Reel cloned');
      onSelect(res.data.composition_id);
    } catch {
      toast.error('Failed to clone reel');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-slate-400 text-sm">Loading your reels...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-100" style={{ fontFamily: 'Playfair Display, serif' }}>My Reels</h2>
          <p className="text-sm text-slate-400 mt-1">{compositions.length} saved reel{compositions.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={onCreate} className="bg-violet-600 hover:bg-violet-500" data-testid="create-new-reel-btn">
          <Plus className="h-4 w-4 mr-2" /> New Reel
        </Button>
      </div>

      {compositions.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-slate-700/50 rounded-xl bg-slate-800/20" data-testid="empty-reels">
          <Film className="h-12 w-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">No reels yet</h3>
          <p className="text-sm text-slate-500 mb-6">Create your first reel to start marketing your listings</p>
          <Button onClick={onCreate} className="bg-violet-600 hover:bg-violet-500" data-testid="create-first-reel-btn">
            <Plus className="h-4 w-4 mr-2" /> Create Reel
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reels-grid">
          {compositions.map((comp) => {
            const trackCount = comp.tracks?.length || 0;
            const totalDur = (comp.tracks || []).reduce((sum, t) => sum + (t.duration || 0), 0);
            const hasRender = !!comp.render_url;

            return (
              <div
                key={comp.composition_id}
                onClick={() => onSelect(comp.composition_id)}
                className="group cursor-pointer rounded-xl border border-slate-700/50 bg-slate-800/30 hover:border-violet-500/50 hover:bg-slate-800/50 transition-all overflow-hidden"
                data-testid={`reel-card-${comp.composition_id}`}
              >
                {/* Thumbnail area */}
                <div className="h-32 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center relative">
                  {hasRender ? (
                    <Play className="h-10 w-10 text-violet-400 group-hover:scale-110 transition-transform" />
                  ) : (
                    <Video className="h-8 w-8 text-slate-600" />
                  )}
                  <div className="absolute top-2 right-2 flex items-center gap-1.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      comp.status === 'rendered' ? 'bg-green-500/20 text-green-400' :
                      comp.status === 'rendering' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {comp.status || 'draft'}
                    </span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-3">
                  <h4 className="text-sm font-medium text-slate-200 truncate">{comp.name || 'Untitled Reel'}</h4>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Video className="h-3 w-3" /> {trackCount} clip{trackCount !== 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {Math.round(totalDur)}s
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-slate-600">{formatDate(comp.updated_at || comp.created_at)}</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost" size="icon"
                          className="h-7 w-7 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`reel-menu-${comp.composition_id}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSelect(comp.composition_id); }}
                          data-testid={`edit-reel-${comp.composition_id}`}>
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => handleClone(e, comp.composition_id)}
                          data-testid={`clone-reel-${comp.composition_id}`}>
                          <Copy className="h-4 w-4 mr-2" /> Clone
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-destructive" onClick={(e) => handleDelete(e, comp.composition_id)}
                          data-testid={`delete-reel-${comp.composition_id}`}>
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ReelEditorPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeListing } = useActiveListing();

  const propertyData = location.state?.propertyData || activeListing || null;
  const reelTemplate = location.state?.reelTemplate || null;
  const searchParams = new URLSearchParams(location.search);
  const compositionId = searchParams.get('comp');
  const hasPropertyParam = searchParams.get('property');
  const templateParam = searchParams.get('template');
  const [showEditor, setShowEditor] = useState(!!compositionId || !!propertyData || !!hasPropertyParam || !!templateParam);
  const [scheduleSignal, setScheduleSignal] = useState(0);

  // React to URL changes (template selection navigates within same component)
  useEffect(() => {
    if (templateParam || compositionId || hasPropertyParam) {
      setShowEditor(true);
    }
  }, [templateParam, compositionId, hasPropertyParam]);

  const handleSelectReel = (compId) => {
    navigate(`/reel-editor?comp=${compId}`);
  };

  const handleCreateNew = () => {
    setShowEditor(true);
  };

  // If URL has comp param or property data, show editor directly
  if (showEditor || compositionId) {
    return (
      // Use dynamic viewport height (dvh) on mobile so the parent height
      // matches the actual visible viewport (excludes the iOS browser chrome
      // that hides on scroll). Without this the editor overflows and pushes
      // the mobile bottom toolbar off-screen, requiring users to scroll.
      <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col overflow-hidden" data-testid="reel-editor-page">
        {/* Minimalist Canva-style top toolbar */}
        <header
          className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-gray-200 flex items-center justify-between px-3 sm:px-4 h-14"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
            <button
              onClick={() => navigate('/dashboard')}
              className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition-all"
              data-testid="reel-editor-home"
              aria-label="Dashboard"
            >
              <Home className="h-[18px] w-[18px] text-gray-700" />
            </button>
            <button
              onClick={() => { setShowEditor(false); navigate('/reel-editor', { replace: true, state: {} }); }}
              className="h-10 px-3 -ml-1 flex items-center gap-1.5 rounded-full hover:bg-gray-100 active:scale-95 transition-all text-sm text-gray-700"
              data-testid="back-to-reels"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Templates</span>
            </button>
            <div className="ml-2 hidden sm:flex items-center gap-2 min-w-0">
              <span className="text-sm font-semibold tracking-tight truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Reel Maker
              </span>
              {propertyData?.address && (
                <span className="text-xs text-gray-400 truncate max-w-[200px]">· {propertyData.address}</span>
              )}
            </div>
          </div>
          <button
            onClick={() => setScheduleSignal((s) => s + 1)}
            className="h-10 px-3 inline-flex items-center gap-1.5 rounded-full hover:bg-gray-100 active:scale-95 transition-all text-sm text-gray-700"
            data-testid="reel-editor-schedule"
            title="Schedule this reel"
          >
            <Calendar className="h-[18px] w-[18px]" />
            <span className="hidden sm:inline">Schedule</span>
          </button>
        </header>

        <div className="flex-1 min-h-0 p-3 sm:p-4 overflow-hidden flex flex-col">
          <ReelEditor
            compositionId={compositionId}
            propertyData={propertyData}
            templateId={templateParam}
            reelTemplate={reelTemplate}
            scheduleSignal={scheduleSignal}
            onSave={(comp) => { /* auto-saved */ }}
          />
        </div>

        <style>{`
          @keyframes zoomIn { from { transform: scale(1); } to { transform: scale(1.2); } }
          @keyframes zoomOut { from { transform: scale(1.2); } to { transform: scale(1); } }
          @keyframes panLeft { from { transform: translateX(0); } to { transform: translateX(-10%); } }
          @keyframes panRight { from { transform: translateX(-10%); } to { transform: translateX(0); } }
        `}</style>
      </div>
    );
  }

  // Show template picker (Reel Maker = templates only)
  return (
    <div className="min-h-screen bg-white pb-24 md:pb-0" data-testid="reels-listing-page" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="noise-texture" />
      <AppNavbar />

      <div className="px-4 sm:px-6 py-6 sm:py-10">
        <div className="max-w-6xl mx-auto mb-8 sm:mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">Vertical video</p>
            <h2
              className="mt-1 text-3xl sm:text-4xl font-light leading-[1.05] tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Pick a <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}>reel</span>.
            </h2>
            <p className="mt-2 text-sm text-gray-400">Choose a template or start from a blank canvas.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/my-reels')}
              className="rounded-full"
              data-testid="view-saved-reels"
            >
              <Film className="h-3.5 w-3.5 mr-1.5" /> My Reels
            </Button>
            <Button
              size="sm"
              onClick={handleCreateNew}
              className="rounded-full bg-[#0B0C10] hover:bg-gray-900"
              data-testid="blank-reel-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Blank Reel
            </Button>
          </div>
        </div>
        <ReelTemplatePicker onSelect={(tplId) => navigate(`/reel-editor?template=${tplId}`)} onCreate={handleCreateNew} />
      </div>
      <MobileTabBar />
    </div>
  );
}
